// drawer/components/CVSSComponent.tsx
import { Fragment } from 'react';
import { useSearchParams } from 'react-router';

import { DashedTag } from '@/components/chariot-ui';
import { PercentageChart } from '@/components/charts/PercentageChart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RenderVector, Section } from '@/sections/threats/components';
import {
  METRIC_GROUP_DESCRIPTIONS,
  PREFERRED_CVSS_VERSIONS,
} from '@/sections/threats/constants';
import type { EnrichedVulnerability } from '@/sections/threats/types';
import { getStatusForScore, parseCVSSData } from '@/sections/threats/utils';
import { cn } from '@/utils/classname';

interface CVSSComponentProps {
  threatIntel?: EnrichedVulnerability;
}

/**
 * CVSS (Common Vulnerability Scoring System) display component
 */
export function CVSSComponent({ threatIntel }: CVSSComponentProps) {
  const { cvss } = threatIntel ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVersionFromUrl = searchParams.get('cvss-version-selection');

  // Use the new utility function
  const parsedCvss = parseCVSSData(cvss);

  const options = Object.entries(parsedCvss)
    .filter(([, cvs]) => cvs.Primary || cvs.Secondary)
    .map(([version]) => ({ label: `Version ${version}`, value: version }));

  const selectedVersion =
    selectedVersionFromUrl ||
    PREFERRED_CVSS_VERSIONS.find(v => v in parsedCvss);
  const selectedOption =
    options.find(o => o.value === selectedVersion) || options[0];
  const selectedCvss = selectedOption?.value
    ? parsedCvss[selectedOption.value]?.Primary ||
      parsedCvss[selectedOption.value]?.Secondary
    : undefined;
  const selectedCvssList = Object.entries(selectedCvss || {});

  return (
    <Section title="CVSS" className="relative">
      {!cvss && (
        <p className="text-sm">No information available at this time</p>
      )}
      {cvss && (
        <>
          {options.length > 0 && (
            <Select
              value={selectedOption?.value ?? ''}
              onValueChange={value => {
                setSearchParams(prev => {
                  prev.set('cvss-version-selection', value);
                  return prev;
                });
              }}
            >
              <SelectTrigger className="absolute top-[-2px] left-16 h-7 w-auto rounded-full px-3 text-xs">
                <SelectValue placeholder={selectedOption?.label} />
              </SelectTrigger>
              <SelectContent>
                {options
                  .filter(option => option.value !== '')
                  .map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          {selectedCvss && (
            <div className="flex flex-col gap-6 p-2">
              {selectedCvssList.map(([metricGroup, data], index) => {
                const score = Number(Math.min(data.score ?? 0, 10).toFixed(1));
                const scoreMeta = getStatusForScore(score);

                return (
                  <Fragment key={index}>
                    <div className="flex min-h-10 gap-8">
                      <div className="flex w-full flex-col gap-2">
                        <h1 className="text-sm font-bold">{metricGroup}</h1>
                        <div className="flex items-center gap-2">
                          <div className="size-[63px] shrink-0">
                            <PercentageChart
                              total={{ name: 'Total', value: 10 }}
                              current={{
                                name: 'CVSS',
                                value: score,
                                lineClassName: scoreMeta.lineClassName,
                              }}
                              displayCurrentValue
                              doNotRoundCurrentValue
                              hidePercentage
                              percentageClassName="text-base font-bold"
                            />
                          </div>
                          <div className="flex w-full flex-col gap-2 p-2">
                            <p className="text-xs">
                              {METRIC_GROUP_DESCRIPTIONS[metricGroup]}
                            </p>
                            <DashedTag
                              size="xs"
                              label={`${scoreMeta.status} Vulnerability`}
                              className={cn(
                                'text-chariot-text-primary border-solid py-0',
                                scoreMeta.tagClassName
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="bg-chariot-stroke w-px shrink-0" />
                      <div className="w-full">
                        <RenderVector
                          vector={data.vector ?? ''}
                          version={selectedOption.value}
                          metricGroup={metricGroup}
                        />
                      </div>
                    </div>
                    {index < selectedCvssList.length - 1 && (
                      <div className="bg-chariot-stroke h-px w-full" />
                    )}
                  </Fragment>
                );
              })}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
